import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Users, LogOut, MapPin, AlertTriangle, Edit, CheckCircle, School, User, Link, Search, Car, Eye, Copy, HeartPulse, PhoneCall, Trash2, CalendarX2 } from 'lucide-react';
import NotificationBell from '../components/NotificationBell';
import MobileActionMenu from '../components/MobileActionMenu';
import { AbsenceDialog } from '../components/AbsenceDialog';
import { MyReviewsDialog } from '../components/reviews/MyReviewsDialog';
import { Star } from 'lucide-react';
import { saTodayISO } from '../lib/dateUtils';
import { useParentPayStatus } from '../components/finance/useParentPayStatus';
import { StatusPill } from '../components/finance/parents/payShared';
import { ReceiptDialog } from '../components/finance/ReceiptDialog';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ParentDashboard() {
  const { user, logout, token } = useAuth();
  const navigate = useNavigate();
  const [kids, setKids] = useState([]);
  const [showReviews, setShowReviews] = useState(false);
  const [trips, setTrips] = useState([]);
  const [schools, setSchools] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showEditAddress, setShowEditAddress] = useState(false);
  const [showChangeSchool, setShowChangeSchool] = useState(false);
  const [selectedKid, setSelectedKid] = useState(null);
  const [newAddress, setNewAddress] = useState('');
  const [newSchoolId, setNewSchoolId] = useState('');
  const [schoolChangeReason, setSchoolChangeReason] = useState('');
  const [showLinkKid, setShowLinkKid] = useState(false);
  const [vehicleRegistration, setVehicleRegistration] = useState('');
  const [kidFullName, setKidFullName] = useState('');
  const [foundKid, setFoundKid] = useState(null);
  const [searchingKids, setSearchingKids] = useState(false);
  const [viewKid, setViewKid] = useState(null);
  const [viewTrip, setViewTrip] = useState(null);
  const [showMedicalAlerts, setShowMedicalAlerts] = useState(false);
  const [medicalAlertsList, setMedicalAlertsList] = useState([]);
  const [medicalNotes, setMedicalNotes] = useState('');
  const [showEmergencyContacts, setShowEmergencyContacts] = useState(false);
  const [emergencyContactsList, setEmergencyContactsList] = useState([]);
  const [absences, setAbsences] = useState([]);
  const payStatus = useParentPayStatus(token);
  const [receiptKid, setReceiptKid] = useState(null);
  const [absenceKid, setAbsenceKid] = useState(null);

  const MEDICAL_CONDITIONS = [
    'Asthma',
    'Diabetes (Type 1)',
    'Diabetes (Type 2)',
    'Epilepsy / Seizures',
    'Peanut allergy',
    'Bee / wasp sting allergy',
    'Other food allergy',
    'Latex allergy',
    'Heart condition',
    'Sickle cell disease',
    'ADHD',
    'Autism spectrum',
    'Anxiety / panic attacks',
    'Chronic medication required',
    'Hearing impairment',
    'Visual impairment',
    'Physical mobility limitation',
  ];

  const openMedicalAlerts = (kid) => {
    setSelectedKid(kid);
    setMedicalAlertsList(kid.medical_alerts || []);
    setMedicalNotes(kid.medical_notes || '');
    setShowMedicalAlerts(true);
  };

  const toggleMedicalCondition = (condition) => {
    setMedicalAlertsList((prev) =>
      prev.includes(condition) ? prev.filter((c) => c !== condition) : [...prev, condition]
    );
  };

  const handleSaveMedicalAlerts = async () => {
    if (!selectedKid) return;
    try {
      await axios.put(`${API_URL}/parent/kid/${selectedKid.kid_id}/medical-alerts`,
        { alerts: medicalAlertsList, notes: medicalNotes },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Medical alerts saved. Driver and transport operator have been notified.');
      setShowMedicalAlerts(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save medical alerts');
    }
  };

  const openEmergencyContacts = (kid) => {
    setSelectedKid(kid);
    const existing = kid.emergency_contacts || [];
    // Pad to always show 3 slots (empty slots are fine)
    const padded = [...existing];
    while (padded.length < 3) padded.push({ name: '', phone: '', relationship: '' });
    setEmergencyContactsList(padded.slice(0, 3));
    setShowEmergencyContacts(true);
  };

  const updateContactField = (index, field, value) => {
    setEmergencyContactsList((prev) => prev.map((c, i) => (i === index ? { ...c, [field]: value } : c)));
  };

  const clearContactSlot = (index) => {
    setEmergencyContactsList((prev) => prev.map((c, i) => (i === index ? { name: '', phone: '', relationship: '' } : c)));
  };

  const handleSaveEmergencyContacts = async () => {
    if (!selectedKid) return;
    // Filter out completely empty slots before sending
    const nonEmpty = emergencyContactsList.filter((c) => (c.name || '').trim() || (c.phone || '').trim());
    try {
      await axios.put(`${API_URL}/parent/kid/${selectedKid.kid_id}/emergency-contacts`,
        { contacts: nonEmpty },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(nonEmpty.length ? 'Emergency contacts saved.' : 'Emergency contacts cleared.');
      setShowEmergencyContacts(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save emergency contacts');
    }
  };

  const fetchData = useCallback(async () => {
    try {
      const [kidsRes, tripsRes, schoolsRes, absencesRes] = await Promise.all([
        axios.get(`${API_URL}/parent/kids`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/trips`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/schools`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/parent/absences`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: [] }))
      ]);
      setAbsences(absencesRes.data || []);
      setKids(kidsRes.data);
      setTrips(tripsRes.data);
      setSchools(schoolsRes.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleUpdateAddress = async () => {
    if (!newAddress.trim()) {
      toast.error('Please enter a new address');
      return;
    }
    try {
      await axios.put(`${API_URL}/parent/kid/${selectedKid.kid_id}/address`, 
        { pickup_address: newAddress },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Address updated! Driver and transport operator have been notified.');
      setShowEditAddress(false);
      setSelectedKid(null);
      setNewAddress('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update address');
    }
  };

  const handleChangeSchool = async () => {
    if (!newSchoolId || !schoolChangeReason.trim()) {
      toast.error('Please select a school and provide a reason');
      return;
    }
    try {
      await axios.put(`${API_URL}/parent/kid/${selectedKid.kid_id}/school`, 
        { new_school_id: newSchoolId, reason: schoolChangeReason },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('School changed! Driver and transport operator have been notified.');
      setShowChangeSchool(false);
      setSelectedKid(null);
      setNewSchoolId('');
      setSchoolChangeReason('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change school');
    }
  };

  const handleSearchKid = async () => {
    if (!vehicleRegistration.trim() || !kidFullName.trim()) {
      toast.error("Please enter both your child's full name and the vehicle registration number");
      return;
    }
    if (kidFullName.trim().length < 2) {
      toast.error("Please enter your child's full name (at least 2 characters)");
      return;
    }
    setSearchingKids(true);
    setFoundKid(null);
    try {
      const response = await axios.get(
        `${API_URL}/parent/search-kid?full_name=${encodeURIComponent(kidFullName)}&vehicle_registration=${encodeURIComponent(vehicleRegistration)}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFoundKid(response.data);
      if (response.data.already_linked) {
        toast.info(`You are already ${response.data.linking_status === 'pending' ? 'pending' : 'linked'} for ${response.data.kid.full_name}.`);
      } else {
        toast.success(`Child found: ${response.data.kid.full_name}. Submit to request a link.`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'No matching child found.');
      setFoundKid(null);
    } finally {
      setSearchingKids(false);
    }
  };

  const handleLinkKid = async () => {
    if (!foundKid?.kid?.kid_id || !vehicleRegistration.trim()) {
      toast.error('Please search for your child first.');
      return;
    }
    try {
      const response = await axios.post(`${API_URL}/parent/link-kid`,
        { kid_id: foundKid.kid.kid_id, vehicle_registration: vehicleRegistration },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message || 'Link request submitted. Awaiting driver/transport approval.');
      setShowLinkKid(false);
      setVehicleRegistration('');
      setKidFullName('');
      setFoundKid(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit link request');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="mt-4 text-muted-foreground">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  const activeTrips = trips.filter(t => t.status === 'active');
  const inProgressTrips = trips.filter(t => t.is_currently_active);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-white" data-testid="parent-dashboard">
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-100/50 sticky top-0 z-50">
        <div className="container mx-auto px-4 sm:px-6 py-4 flex items-center justify-between gap-2">
          <div className="flex items-center gap-3">
            <img src="/ntwanayam-logo.jpg" alt="NtwanaYam Logo" className="w-12 h-12 rounded-xl object-cover shadow-sm hidden sm:block" data-testid="brand-logo" />
            <div>
              <h1 className="text-xl sm:text-2xl font-heading font-bold text-foreground" data-testid="dashboard-title">Parent Dashboard</h1>
              <p className="text-sm text-muted-foreground" data-testid="user-name">Welcome, {user.full_name}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <MobileActionMenu title="Menu">
            <Button variant="default" onClick={() => setShowLinkKid(true)} className="rounded-full" data-testid="link-child-button">
              <Link className="w-4 h-4 mr-2" />
              Link My Child
            </Button>
            <Button variant="outline" onClick={() => navigate('/complaints')} className="rounded-full" data-testid="complaints-button">
              <AlertTriangle className="w-4 h-4 mr-2" />
              Complaints
            </Button>
            <Button variant="outline" onClick={() => setShowReviews(true)} className="rounded-full text-amber-700 border-amber-200 hover:bg-amber-50" data-testid="write-review-button">
              <Star className="w-4 h-4 mr-2" /> Write Review
            </Button>
            <Button variant="outline" onClick={() => navigate('/profile')} className="rounded-full" data-testid="profile-button">
              <User className="w-4 h-4 mr-2" />
              Profile
            </Button>
            <Button variant="outline" onClick={logout} className="rounded-full" data-testid="logout-button">
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
            </MobileActionMenu>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 sm:px-6 py-6 sm:py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8" data-testid="stats-grid">
          <Card className="hover:shadow-lg transition-all duration-300">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">My Children</CardTitle>
              <Users className="h-5 w-5 text-blue-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{kids.length}</div>
              <p className="text-xs text-muted-foreground mt-1">Registered for transport</p>
            </CardContent>
          </Card>
          <Card className="hover:shadow-lg transition-all duration-300">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Active Trips</CardTitle>
              <MapPin className="h-5 w-5 text-emerald-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold">{activeTrips.length}</div>
              <p className="text-xs text-muted-foreground mt-1">Scheduled trips</p>
            </CardContent>
          </Card>
          <Card className="hover:shadow-lg transition-all duration-300">
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">In Progress</CardTitle>
              <CheckCircle className="h-5 w-5 text-green-500" />
            </CardHeader>
            <CardContent>
              <div className="text-3xl font-bold text-green-600">{inProgressTrips.length}</div>
              <p className="text-xs text-muted-foreground mt-1">Currently running</p>
            </CardContent>
          </Card>
        </div>

        {/* Kids Section */}
        <Card className="shadow-lg mb-8" data-testid="kids-section">
          <CardHeader>
            <CardTitle className="text-2xl font-heading">My Children</CardTitle>
            <CardDescription>View your children&apos;s transport details, update pickup addresses, or change schools</CardDescription>
          </CardHeader>
          <CardContent>
            {kids.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground" data-testid="no-kids">
                <Users className="w-16 h-16 mx-auto mb-4 text-emerald-300" />
                <p>No children registered yet</p>
                <p className="text-sm mt-2">Your driver will register your children in the system</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {kids.map((kid) => {
                  const isPending = kid.linking_status === 'pending';
                  const kidAbsences = absences.filter(a => a.kid_id === kid.kid_id);
                  const absentToday = kidAbsences.some(a => a.date === saTodayISO());
                  return (
                  <div key={kid.kid_id} className={`p-4 border rounded-xl hover:shadow-md transition-all ${isPending ? 'bg-amber-50 border-amber-200' : ''}`} data-testid={`kid-item-${kid.kid_id}`}>
                    <div className="flex flex-col gap-3">
                      <div className="flex items-start justify-between">
                        <div>
                          <h3 className="font-semibold text-lg" data-testid={`kid-name-${kid.kid_id}`}>{kid.full_name}</h3>
                          <div className="flex items-center gap-2 mt-1">
                            <School className="w-4 h-4 text-blue-500" />
                            <p className="text-sm text-muted-foreground">{kid.school_name} - {kid.grade}</p>
                          </div>
                          {!isPending && (payStatus.status[kid.kid_id] || payStatus.receipts.has(kid.kid_id)) && (
                            <div className="mt-2 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground" data-testid={`kid-pay-status-${kid.kid_id}`}>
                              {payStatus.status[kid.kid_id] && (
                                <span className="flex items-center gap-2 whitespace-nowrap">This month&apos;s fee: <StatusPill status={payStatus.status[kid.kid_id]} testid={`kid-pay-pill-${kid.kid_id}`} /></span>
                              )}
                              {payStatus.receipts.has(kid.kid_id) && (
                                <button type="button" onClick={() => setReceiptKid(kid)} className="underline text-emerald-700 font-medium" data-testid={`kid-receipts-btn-${kid.kid_id}`}>Receipts</button>
                              )}
                            </div>
                          )}
                        </div>
                        {isPending ? (
                          <Badge className="bg-amber-100 text-amber-800 border-amber-300" data-testid={`kid-pending-${kid.kid_id}`}>Pending Approval</Badge>
                        ) : (kid.medical_alerts?.length > 0 || kid.medical_notes) ? (
                          <Badge className="bg-red-100 text-red-800 border-red-300 gap-1" data-testid={`kid-medical-badge-${kid.kid_id}`}>
                            <HeartPulse className="w-3 h-3" /> Medical alerts
                          </Badge>
                        ) : (
                          <Badge variant="outline" className="text-xs">{kid.status}</Badge>
                        )}
                      </div>

                      {!isPending && (kid.medical_alerts?.length > 0 || kid.medical_notes) && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3" data-testid={`medical-summary-${kid.kid_id}`}>
                          <div className="flex items-center gap-2 text-red-800 font-semibold text-sm mb-1">
                            <HeartPulse className="w-4 h-4" /> Medical Alerts (visible to driver)
                          </div>
                          {kid.medical_alerts?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mb-1">
                              {kid.medical_alerts.map((a) => (
                                <Badge key={a} className="bg-red-600 text-white text-xs">{a}</Badge>
                              ))}
                            </div>
                          )}
                          {kid.medical_notes && (
                            <p className="text-sm text-red-900 whitespace-pre-wrap"><strong>Notes:</strong> {kid.medical_notes}</p>
                          )}
                        </div>
                      )}

                      {isPending && (
                        <div className="bg-amber-100 border border-amber-300 rounded-lg p-3 text-sm text-amber-900" data-testid={`pending-banner-${kid.kid_id}`}>
                          <AlertTriangle className="w-4 h-4 inline-block mr-2" />
                          Your link to this child is <strong>pending approval</strong> by the driver or transport operator. You will be able to update details once approved. Auto-expires in 3 working days if not approved.
                        </div>
                      )}

                      {!isPending && kidAbsences.length > 0 && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900" data-testid={`kid-absence-summary-${kid.kid_id}`}>
                          <CalendarX2 className="w-4 h-4 inline-block mr-2" />
                          {absentToday ? <strong>Marked absent today. </strong> : null}
                          {kidAbsences.length} upcoming absent day(s): {kidAbsences.slice(0, 5).map(a => a.date).join(', ')}{kidAbsences.length > 5 ? '…' : ''}
                        </div>
                      )}

                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-muted-foreground">Pickup Address:</p>
                        <p className="text-sm font-medium" data-testid={`kid-address-${kid.kid_id}`}>{kid.pickup_address}</p>
                      </div>

                      <div className="flex gap-2 flex-wrap">
                        <Button size="sm" variant="outline" className="rounded-full" onClick={() => setViewKid(kid)} data-testid={`view-kid-${kid.kid_id}`}>
                          <Eye className="w-4 h-4 mr-1" /> View
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-full" disabled={isPending} onClick={() => {
                          setSelectedKid(kid);
                          setNewAddress(kid.pickup_address);
                          setShowEditAddress(true);
                        }} data-testid={`edit-address-${kid.kid_id}`}>
                          <Edit className="w-4 h-4 mr-1" /> Edit Address
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-full" disabled={isPending} onClick={() => {
                          setSelectedKid(kid);
                          setNewSchoolId('');
                          setSchoolChangeReason('');
                          setShowChangeSchool(true);
                        }} data-testid={`change-school-${kid.kid_id}`}>
                          <School className="w-4 h-4 mr-1" /> Change School
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-full text-amber-700 border-amber-300 hover:bg-amber-50" disabled={isPending} onClick={() => setAbsenceKid(kid)} data-testid={`mark-absent-btn-${kid.kid_id}`}>
                          <CalendarX2 className="w-4 h-4 mr-1" /> Mark Absent
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-full text-red-700 border-red-200 hover:bg-red-50" disabled={isPending} onClick={() => openMedicalAlerts(kid)} data-testid={`medical-alerts-btn-${kid.kid_id}`}>
                          <HeartPulse className="w-4 h-4 mr-1" /> Medical Alerts
                        </Button>
                        <Button size="sm" variant="outline" className="rounded-full text-blue-700 border-blue-200 hover:bg-blue-50" disabled={isPending} onClick={() => openEmergencyContacts(kid)} data-testid={`emergency-contacts-btn-${kid.kid_id}`}>
                          <PhoneCall className="w-4 h-4 mr-1" /> Emergency Contacts
                        </Button>
                      </div>
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Trips Section */}
        <Card className="shadow-lg" data-testid="trips-section">
          <CardHeader>
            <CardTitle className="text-2xl font-heading">Trip Status</CardTitle>
            <CardDescription>View your children&apos;s trip status</CardDescription>
          </CardHeader>
          <CardContent>
            {trips.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground" data-testid="no-trips">
                <MapPin className="w-16 h-16 mx-auto mb-4 text-emerald-300" />
                <p>No trips scheduled yet</p>
              </div>
            ) : (
              <div className="space-y-4">
                {trips.map((trip) => (
                  <div key={trip.trip_id} className="p-4 border rounded-xl hover:shadow-md transition-all" data-testid={`trip-item-${trip.trip_id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-3">
                          <h3 className="font-semibold text-lg">{trip.route_name}</h3>
                          <Badge variant="outline">{trip.term}</Badge>
                          <Badge className={trip.is_currently_active ? 'bg-green-100 text-green-700 animate-pulse' : 'bg-blue-100 text-blue-700'}>
                            {trip.is_currently_active ? 'Trip In Progress!' : trip.status}
                          </Badge>
                        </div>
                        <p className="text-sm text-muted-foreground mt-1">
                          {trip.school_name} | {trip.start_date} to {trip.end_date} at {trip.scheduled_time}
                        </p>
                        <p className="text-xs text-muted-foreground mt-1">
                          {trip.sessions?.length || 0} trip session(s) completed this term
                        </p>
                      </div>
                      <Button size="sm" variant="outline" className="rounded-full" onClick={() => setViewTrip(trip)} data-testid={`view-trip-${trip.trip_id}`}>
                        <Eye className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </main>
      <MyReviewsDialog open={showReviews} onOpenChange={setShowReviews} token={token} />
      <ReceiptDialog kid={receiptKid} token={token} onClose={() => setReceiptKid(null)} />
      <AbsenceDialog kid={absenceKid} absences={absences} token={token} onClose={() => setAbsenceKid(null)} onChanged={fetchData} />

      {/* Edit Address Dialog */}
      <Dialog open={showEditAddress} onOpenChange={setShowEditAddress}>
        <DialogContent data-testid="edit-address-dialog">
          <DialogHeader>
            <DialogTitle>Update Pickup Address</DialogTitle>
            <DialogDescription>
              Update the pickup address for {selectedKid?.full_name}. 
              The driver and transport operator will be notified of this change.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>New Pickup Address</Label>
              <Input
                value={newAddress}
                onChange={(e) => setNewAddress(e.target.value)}
                placeholder="Enter new pickup address"
                data-testid="new-address-input"
              />
            </div>
            <div className="bg-amber-50 p-3 rounded-lg text-amber-800 text-sm">
              <AlertTriangle className="w-4 h-4 inline-block mr-2" />
              Changing the address will notify your driver and transport operator immediately.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowEditAddress(false); setSelectedKid(null); }}>Cancel</Button>
            <Button onClick={handleUpdateAddress} data-testid="save-address-btn">Save Address</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change School Dialog */}
      <Dialog open={showChangeSchool} onOpenChange={setShowChangeSchool}>
        <DialogContent data-testid="change-school-dialog">
          <DialogHeader>
            <DialogTitle>Change School</DialogTitle>
            <DialogDescription>
              Change the school for {selectedKid?.full_name}. 
              The driver and transport operator will be notified of this change.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Current School</Label>
              <Input value={selectedKid?.school_name || ''} disabled className="bg-slate-50" />
            </div>
            <div className="space-y-2">
              <Label>New School *</Label>
              <Select value={newSchoolId} onValueChange={setNewSchoolId}>
                <SelectTrigger data-testid="new-school-select">
                  <SelectValue placeholder="Select new school" />
                </SelectTrigger>
                <SelectContent>
                  {schools.filter(s => s.school_id !== selectedKid?.school_id).map(school => (
                    <SelectItem key={school.school_id} value={school.school_id}>{school.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Reason for Change *</Label>
              <Textarea
                value={schoolChangeReason}
                onChange={(e) => setSchoolChangeReason(e.target.value)}
                placeholder="Please provide a reason for changing the school..."
                rows={3}
                data-testid="school-change-reason"
              />
            </div>
            <div className="bg-amber-50 p-3 rounded-lg text-amber-800 text-sm">
              <AlertTriangle className="w-4 h-4 inline-block mr-2" />
              Changing the school will notify your driver and transport operator immediately.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowChangeSchool(false); setSelectedKid(null); }}>Cancel</Button>
            <Button onClick={handleChangeSchool} data-testid="save-school-btn">Change School</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Child Dialog */}
      <Dialog open={showLinkKid} onOpenChange={setShowLinkKid}>
        <DialogContent className="max-w-lg" data-testid="link-child-dialog">
          <DialogHeader>
            <DialogTitle>Link My Child</DialogTitle>
            <DialogDescription>
              Search for your child by full name and vehicle registration number. Your driver or transport operator will review and approve the link within 3 working days.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-blue-50 p-3 rounded-lg text-blue-800 text-sm">
              <Users className="w-4 h-4 inline-block mr-2" />
              Ask your driver for the <strong>vehicle number plate</strong>, then enter your child&apos;s <strong>full name and surname</strong> exactly as registered.
            </div>
            <div className="space-y-2">
              <Label>Child&apos;s Full Name (Name & Surname) *</Label>
              <Input
                value={kidFullName}
                onChange={(e) => setKidFullName(e.target.value)}
                placeholder="e.g., Thabo Mokoena"
                data-testid="kid-name-search-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Vehicle Registration Number (Number Plate) *</Label>
              <div className="flex gap-2">
                <Input
                  value={vehicleRegistration}
                  onChange={(e) => setVehicleRegistration(e.target.value.toUpperCase())}
                  placeholder="e.g., CA 123-456"
                  className="flex-1"
                  data-testid="vehicle-registration-input"
                />
                <Button variant="outline" onClick={handleSearchKid} disabled={searchingKids} data-testid="search-kids-btn">
                  <Search className="w-4 h-4 mr-1" />
                  {searchingKids ? 'Searching...' : 'Search'}
                </Button>
              </div>
            </div>

            {foundKid?.found && (
              <div className="border-2 border-emerald-300 bg-emerald-50 rounded-lg p-4" data-testid="found-kid-card">
                <p className="text-xs text-emerald-600 font-medium uppercase mb-1">Match found</p>
                <p className="font-semibold text-lg">{foundKid.kid.full_name}</p>
                <p className="text-sm text-muted-foreground">{foundKid.kid.school_name} {foundKid.kid.grade ? `\u2014 ${foundKid.kid.grade}` : ''}</p>
                {foundKid.already_linked && (
                  <p className="text-xs text-amber-700 mt-2">
                    You are already {foundKid.linking_status === 'pending' ? 'pending approval' : 'linked'} to this child.
                  </p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowLinkKid(false); setVehicleRegistration(''); setKidFullName(''); setFoundKid(null); }}>
              Cancel
            </Button>
            <Button onClick={handleLinkKid} disabled={!foundKid?.found || foundKid?.already_linked} data-testid="link-child-submit-btn">
              <Link className="w-4 h-4 mr-2" />
              Submit Link Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Medical Alerts Dialog */}
      <Dialog open={showMedicalAlerts} onOpenChange={setShowMedicalAlerts}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="medical-alerts-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <HeartPulse className="w-5 h-5" /> Medical Alerts for {selectedKid?.full_name}
            </DialogTitle>
            <DialogDescription>
              Tick every condition that applies. Your driver and transport operator will see this information so they can respond safely in an emergency.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-5">
            <div className="space-y-2">
              <Label>Conditions</Label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 max-h-72 overflow-y-auto border rounded-lg p-3 bg-slate-50">
                {MEDICAL_CONDITIONS.map((c) => (
                  <label key={c} className="flex items-center gap-2 text-sm cursor-pointer p-1.5 rounded hover:bg-white transition" data-testid={`medical-condition-${c.replace(/[^a-z0-9]+/gi,'-').toLowerCase()}`}>
                    <Checkbox
                      checked={medicalAlertsList.includes(c)}
                      onCheckedChange={() => toggleMedicalCondition(c)}
                    />
                    <span>{c}</span>
                  </label>
                ))}
              </div>
              {medicalAlertsList.length > 0 && (
                <div className="flex flex-wrap gap-1 mt-2">
                  {medicalAlertsList.map((a) => (
                    <Badge key={a} className="bg-red-600 text-white">{a}</Badge>
                  ))}
                </div>
              )}
            </div>

            <div className="space-y-2">
              <Label>Additional Notes / Instructions (optional)</Label>
              <Textarea
                value={medicalNotes}
                onChange={(e) => setMedicalNotes(e.target.value)}
                placeholder="e.g., Inhaler in bag pocket. Peanut = severe anaphylaxis. Emergency contact: +27 82 123 4567."
                rows={4}
                maxLength={1000}
                data-testid="medical-notes-input"
              />
              <p className="text-xs text-muted-foreground">{medicalNotes.length}/1000 characters</p>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-blue-900 text-xs">
              <AlertTriangle className="w-4 h-4 inline-block mr-1" />
              This information is shared with the driver and transport operator responsible for your child. Please keep it accurate.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMedicalAlerts(false)}>Cancel</Button>
            <Button onClick={handleSaveMedicalAlerts} data-testid="save-medical-alerts-btn" className="bg-red-600 hover:bg-red-700">
              Save Medical Alerts
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Emergency Contacts Dialog */}
      <Dialog open={showEmergencyContacts} onOpenChange={setShowEmergencyContacts}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="emergency-contacts-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-700">
              <PhoneCall className="w-5 h-5" /> Emergency Contacts for {selectedKid?.full_name}
            </DialogTitle>
            <DialogDescription>
              Add up to 3 people the driver or transport operator can call in an emergency. All fields are optional — you can leave slots blank or update them anytime.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {emergencyContactsList.map((contact, index) => (
              <div key={index} className="border rounded-lg p-3 bg-slate-50 space-y-2" data-testid={`emergency-contact-slot-${index}`}>
                <div className="flex items-center justify-between">
                  <Label className="text-xs font-semibold text-muted-foreground uppercase">Contact {index + 1}</Label>
                  {(contact.name || contact.phone || contact.relationship) && (
                    <Button size="sm" variant="ghost" className="h-6 px-2 text-red-600 hover:text-red-700 hover:bg-red-50" onClick={() => clearContactSlot(index)} data-testid={`clear-contact-${index}`}>
                      <Trash2 className="w-3 h-3 mr-1" /> Clear
                    </Button>
                  )}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  <div>
                    <Label className="text-xs">Name</Label>
                    <Input
                      value={contact.name}
                      onChange={(e) => updateContactField(index, 'name', e.target.value)}
                      placeholder="e.g., Jane Doe"
                      maxLength={100}
                      data-testid={`contact-name-${index}`}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Relationship</Label>
                    <Input
                      value={contact.relationship}
                      onChange={(e) => updateContactField(index, 'relationship', e.target.value)}
                      placeholder="e.g., Mother, Uncle, Doctor"
                      maxLength={50}
                      data-testid={`contact-rel-${index}`}
                    />
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Phone Number</Label>
                  <Input
                    value={contact.phone}
                    onChange={(e) => updateContactField(index, 'phone', e.target.value)}
                    placeholder="+27 82 123 4567"
                    maxLength={30}
                    data-testid={`contact-phone-${index}`}
                  />
                </div>
              </div>
            ))}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-blue-900 text-xs">
              <AlertTriangle className="w-4 h-4 inline-block mr-1" />
              Emergency contacts are visible to the driver and transport operator responsible for your child. You can update or remove them anytime.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEmergencyContacts(false)}>Cancel</Button>
            <Button onClick={handleSaveEmergencyContacts} data-testid="save-emergency-contacts-btn" className="bg-blue-600 hover:bg-blue-700">
              Save Contacts
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Trip Dialog */}
      <Dialog open={!!viewTrip} onOpenChange={(open) => { if (!open) setViewTrip(null); }}>
        <DialogContent className="max-w-lg" data-testid="view-trip-dialog">
          <DialogHeader>
            <DialogTitle>Trip Details</DialogTitle>
          </DialogHeader>
          {viewTrip && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Route Name</Label>
                  <p className="text-sm font-medium">{viewTrip.route_name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Trip Type</Label>
                  <p className="text-sm font-medium">{viewTrip.trip_type === 'morning' ? 'Morning (Home → School)' : 'Afternoon (School → Home)'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">School</Label>
                  <p className="text-sm font-medium">{viewTrip.school_name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Term</Label>
                  <p className="text-sm font-medium">{viewTrip.term}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Start Date</Label>
                  <p className="text-sm font-medium">{viewTrip.start_date}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">End Date</Label>
                  <p className="text-sm font-medium">{viewTrip.end_date}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Scheduled Time</Label>
                  <p className="text-sm font-medium">{viewTrip.scheduled_time}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Driver</Label>
                  <p className="text-sm font-medium">{viewTrip.driver_name || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Vehicle</Label>
                  <p className="text-sm font-medium">{viewTrip.vehicle_registration || 'N/A'}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Sessions Completed</Label>
                  <p className="text-sm font-medium">{viewTrip.sessions?.length || 0}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-muted-foreground">Status</Label>
                  <div className="mt-1">
                    <Badge className={viewTrip.is_currently_active ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}>
                      {viewTrip.is_currently_active ? 'Trip In Progress' : viewTrip.status}
                    </Badge>
                  </div>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* View Kid Dialog */}
      <Dialog open={!!viewKid} onOpenChange={(open) => { if (!open) setViewKid(null); }}>
        <DialogContent className="max-w-lg" data-testid="view-kid-dialog">
          <DialogHeader>
            <DialogTitle>Child Details</DialogTitle>
          </DialogHeader>
          {viewKid && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-muted-foreground">Full Name</Label>
                  <p className="text-sm font-medium">{viewKid.full_name}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Grade</Label>
                  <p className="text-sm font-medium">{viewKid.grade}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-muted-foreground">School</Label>
                  <p className="text-sm font-medium">{viewKid.school_name}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-muted-foreground">Pickup Address (Morning)</Label>
                  <p className="text-sm font-medium">{viewKid.pickup_address}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-muted-foreground">Drop-off Address (Afternoon)</Label>
                  <p className="text-sm font-medium">{viewKid.drop_off_address || viewKid.pickup_address}</p>
                </div>
                <div className="col-span-2">
                  <Label className="text-muted-foreground">Kid ID</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <code className="flex-1 bg-slate-100 p-2 rounded text-xs break-all">{viewKid.kid_id}</code>
                    <Button 
                      size="sm" 
                      variant="outline"
                      onClick={() => {
                        navigator.clipboard.writeText(viewKid.kid_id);
                        toast.success('Kid ID copied!');
                      }}
                    >
                      <Copy className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <Badge className="mt-1" variant={viewKid.status === 'active' ? 'default' : 'secondary'}>{viewKid.status}</Badge>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
